import { getRotatedDimensions } from '@common-util/texture-util';
import { DisplacementMapFeature } from '@models/classes';
import { FileTextureLayer } from '@models/classes/materials/file-texture-layer';
import { MaterialNode } from '@models/classes/materials/material-node';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { TextureParameters } from '../texture-stack';
import { IDrawOperations, TextureLayerRenderer } from './texture';

export class FileTextureLayerRenderer extends TextureLayerRenderer<FileTextureLayer> {
    modelToken = Token.FileTextureLayer;

    paintTexture(
        texture: FileTextureLayer,
        context: CanvasRenderingContext2D,
        parameters: TextureParameters,
        operations: IDrawOperations
    ) {
        if (texture._buffer) {
            return this.drawSourceToCanvas(texture, texture._buffer, context, parameters, operations);
        }
    }

    textureHasContent(node: FileTextureLayer) {
        return node.path !== '';
    }

    getNativeSize(texture: FileTextureLayer) {
        if (texture._buffer) {
            const width = Math.max(texture._buffer.width, 1),
                height = Math.max(texture._buffer.height, 1);

            if (texture.wAngle !== 0) {
                return getRotatedDimensions(width, height, texture.wAngle);
            } else {
                return { width, height };
            }
        }
        return { width: 1, height: 1 };
    }

    update3dInternal(node: ITrackedClass<FileTextureLayer>) {
        if (
            ((node.changes.has('height') || node.changes.has('width')) && node.path.endsWith('.svg')) ||
            node.changes.has('path') ||
            (node.changes.has('enabled') && !node._buffer) ||
            node.changes.has('_init')
        ) {
            node._needsImageUpdate = true;
        }

        if (node._needsImageUpdate && node.enabled && node.path && node._init) {
            this.loadTextureBuffer(node);
        }
        super.update3dInternal(node);
    }

    private loadTextureBuffer(node: ITrackedClass<FileTextureLayer>) {
        if (node._parent instanceof MaterialNode || node._parent instanceof DisplacementMapFeature) {
            node._needsImageUpdate = false;
            if (node._buffer) {
                node.trackChanges(false);
                node._buffer = undefined; // Remove the buffer so the texture stack doesn't think the image is already loaded if the image was changed
                node.trackChanges(true);
            }
            const originalPath = node.path;
            node._parent._materialService.getTextureImage(node).subscribe(resp => {
                if(node.path !== originalPath) {
                    // If another code path modified the texture since we started loading, ignore the update.
                    // There is abort code in the texture image handler as well but not once the texture is loaded and is being processed.
                    return;
                }

                node._buffer = resp.bitmap;
                node._bufferSource = resp.url;
            });
        } else {
            console.warn('Texure is not attached to a material node. Cannot load texture image bitmap.');
        }
    }
}
